import { useContext, useState, useCallback, useEffect } from "react";
import { SketchPicker } from "react-color";
import {
  Form,
  Select,
  Slider,
  Button,
  Popover,
  Divider,
  Input,
  Alert,
  Radio,
  Switch,
  Checkbox,
} from "antd";
import {
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import ContentPadding from "../ContentConfig/ContentPadding";
import { Context, useEditorContext } from "../EditorContext";
import ImageUpploadField from "../WechatTextImage/ImageUploadField";
import { rgbaToHex, toRGBA } from "../parser/utils";
import ContentID from "../Common/ContentID";
import { listLandingPage } from "../Service/MediaMakerService";
import useSelectSearchData from "../hooks/useSelectSearchData";

const PHONE_REGEXP =
  /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{2,8}$/;

const { Option } = Select;

const locale = "en-US";

const BORDER_OPTIONS_EN = [
  {
    label: "Line",
    value: "solid",
  },
  {
    label: "Dash",
    value: "dashed",
  },
  {
    label: "Dotted",
    value: "dotted",
  },
  {
    label: "Double",
    value: "double",
  },
  {
    label: "Groove",
    value: "groove",
  },
  {
    label: "Ridge",
    value: "ridge",
  },
  {
    label: "Inset",
    value: "inset",
  },
  {
    label: "Outset",
    value: "Outset",
  },
];

const BORDER_OPTIONS_CN = [
  {
    label: "直线",
    value: "solid",
  },
  {
    label: "破折线",
    value: "dashed",
  },
  {
    label: "点状线",
    value: "dotted",
  },
  {
    label: "双划线",
    value: "double",
  },
  {
    label: "3D凹槽",
    value: "groove",
  },
  {
    label: "3D垄状",
    value: "ridge",
  },
  {
    label: "3D内嵌",
    value: "inset",
  },
  {
    label: "3D外嵌'",
    value: "Outset",
  },
];

const BORDER_OPTIONS_HK = [
  {
    label: "直線",
    value: "solid",
  },
  {
    label: "破折線",
    value: "dashed",
  },
  {
    label: "點狀線",
    value: "dotted",
  },
  {
    label: "雙劃線",
    value: "double",
  },
  {
    label: "3D凹槽",
    value: "groove",
  },
  {
    label: "3D壟狀",
    value: "ridge",
  },
  {
    label: "3D內嵌",
    value: "inset",
  },
  {
    label: "3D外嵌'",
    value: "Outset",
  },
];

const BORDER_OPTIONS = {
  "en-US": BORDER_OPTIONS_EN,
  "zh-CN": BORDER_OPTIONS_CN,
  "zh-TW": BORDER_OPTIONS_HK,
};

const ColorPicker = ({ value, onChange, onChangeBackgroundColor }) => {
  const [color, setColor] = useState(value);

  useEffect(() => {
    setColor(value);
  }, [value, setColor]);

  const handleChange = useCallback(
    (seColor) => {
      const { rgb } = seColor;
      setColor(rgb);
      onChange(rgb);
      onChangeBackgroundColor(rgb);
    },
    [onChange, onChangeBackgroundColor]
  );

  return <SketchPicker color={color} onChange={handleChange} />;
};

export const ColorPickerFormItem = ({
  label,
  name,
  color: initColor,
  disabled = false,
}) => {
  const [color, setColor] = useState(initColor);
  const { setFieldValue, submit } = Form.useFormInstance();
  const { hex, alpha } = rgbaToHex(color);
  const clearColor = useCallback(() => {
    setColor(void 0);
    setFieldValue(name, "");
    submit();
  }, []);

  if (disabled) {
    return (
      <Form.Item label={label}>
        <div className="color-picker-wrapper" style={{ opacity: 0.5, cursor: "unset" }}>
          <div className="color-picker-panel">
            <div
              className="color-picker-panel-inner"
              style={{ backgroundColor: toRGBA(color) }}
            />
          </div>
          <div
            className="color-picker-text"
            style={{ textAlign: alpha < 100 ? "left" : "center" }}
          >
            {!hex && !alpha && "No Color"}
            {hex}
            {alpha < 100 ? "," + alpha + "%" : ""}
          </div>
        </div>
      </Form.Item>
    );
  }

  return (
    <Form.Item label={label}>
      {/* for form registerField first time */}
      <Form.Item style={{ display: "none" }} name={name}>
        <Input />
      </Form.Item>
      <div style={{ display: "flex" }}>
        <Popover
          content={
            <Form.Item name={name}>
              <ColorPicker
                onChangeBackgroundColor={(color) => {
                  setColor(color);
                }}
              />
            </Form.Item>
          }
          trigger="click"
        >
          <div className="color-picker-wrapper">
            <div className="color-picker-panel">
              <div
                className="color-picker-panel-inner"
                style={{ backgroundColor: toRGBA(color) }}
              />
            </div>
            <div
              className="color-picker-text"
              style={{ textAlign: alpha < 100 ? "left" : "center" }}
            >
              {!hex && !alpha && "No Color"}
              {hex}
              {alpha < 100 ? "," + alpha + "%" : ""}
            </div>
          </div>
        </Popover>
        {hex && alpha && (
          <Button
            onClick={clearColor}
            shape="circle"
            size="small"
            disabled={disabled}
            style={{ marginTop: 4, marginLeft: 5 }}
            icon={<CloseOutlined />}
          />
        )}
      </div>
    </Form.Item>
  );
};

const EmailInputField = () => {
  return (
    <>
      <Form.Item
        label="Email address"
        name={"email"}
        rules={[{ type: "email", message: "Invalid email" }]}
      >
        <Input maxLength={256} />
      </Form.Item>
      <Form.Item label="Message Subject" name={"emailSubject"}>
        <Input maxLength={128} />
      </Form.Item>
      <Form.Item label="Message Body" name={"emailBody"}>
        <Input maxLength={512} />
      </Form.Item>
    </>
  );
};

export const LandingPageList = ({rules}) => {
  const { shareRefs } = useEditorContext();
  const { setKeyword, data, addCache, loading } = useSelectSearchData({
    service: (params) => listLandingPage(params, shareRefs?.requestInfo),
  });
  return (
    <Form.Item rules={rules} label="Landing page" name="landingPage">
      <Select
        showSearch
        optionFilterProp="label"
        onSearch={setKeyword}
        onChange={(v) => {
          addCache(v);
        }}
        loading={loading}
        onFocus={() => setKeyword("")}
        options={data}
      />
    </Form.Item>
  );
};

export const getContentIDList = (tree) => {
  const contentIDList = [];
  tree.traverseDF((node) => {
    const contentID = node.data.contentID;
    if (contentID) {
      contentIDList.push({ label: contentID, value: contentID });
    }
  });
  return contentIDList;
};

export const LinkInputFields = () => {
  const { shareRefs } = useEditorContext();
  const contentIDList = getContentIDList(shareRefs.tree);
  return (
    <>
      <Form.Item name={"linkUrlType"} label="Link type">
        <Select
          defaultValue="web"
          style={{ width: "100%" }}
          options={[
            { label: "Url", value: "web" },
            { label: "Landing page", value: "landing" },
            { label: "Email", value: "email" },
            { label: "Phone", value: "phone" },
            { label: "Section", value: "anchor" },
          ]}
        />
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          const type = getFieldValue("linkUrlType") || "web";
          if (type === "web") {
            return (
              <Form.Item
                rules={[{ type: "url", message: "Invalid URL" }]}
                label="Url"
                name={"linkUrl"}
              >
                <Input maxLength={2048} />
              </Form.Item>
            );
          } else if (type === "email") {
            return <EmailInputField />;
          } else if (type === "landing") {
            return <LandingPageList />;
          } else if (type === "phone") {
            return (
              <Form.Item
                label="Phone number"
                name={"phone"}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      if (PHONE_REGEXP.test(value)) return Promise.resolve();
                      return Promise.reject("Invalid phone no.");
                    },
                  },
                ]}
              >
                <Input maxLength={32} />
              </Form.Item>
            );
          } else if (type === "anchor") {
            return (
              <Form.Item label="Content" name={"linkAnchor"}>
                <Select options={contentIDList} />
              </Form.Item>
            );
          }
          return null;
        }}
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ setFieldValue, getFieldValue }) => {
          const linkUrlType = getFieldValue("linkUrlType");
          if (linkUrlType === "anchor") {
            setTimeout(() => {
              setFieldValue("linkNewTab", false);
            });
          }
          return (
            <Form.Item name="linkNewTab" valuePropName="checked">
              <Checkbox disabled={linkUrlType === "anchor"}>
                Open link in new tab
              </Checkbox>
            </Form.Item>
          );
        }}
      </Form.Item>
    </>
  );
};

export default () => {
  const { editorType } = useContext(Context);
  return (
    <>
      <ImageUpploadField name="url" label="URL" rules={[{ required: true }]} />
      {editorType === "wechat" && (
        <>
          <Form.Item
            label="Link"
            name="imageLink"
            rules={[
              { type: "url", message: "Invalid Url" },
              { max: 2000, message: "Max Length 2000" },
            ]}
          >
            <Input />
          </Form.Item>
          <Alert
            style={{ marginBottom: 15 }}
            message="WeChat Link Limit"
            type="info"
          />
        </>
      )}
      <Form.Item label={"Alt Text"} name="alt">
        <Input addonBefore="Alt Text" />
      </Form.Item>
      <div style={{ display: "flex", gap: 10 }}>
        <label>Auto width</label>
        <Form.Item noStyle valuePropName="checked" name={"autoWidth"}>
          <Switch />
        </Form.Item>
      </div>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          const auto = getFieldValue("autoWidth");
          if (!auto) {
            return (
              <Form.Item noStyle name="width">
                <Slider
                  style={{ width: "90%" }}
                  min={20}
                  max={100}
                  marks={{ 20: "-", 100: "+" }}
                  tipFormatter={(v) => `${v}%`}
                />
              </Form.Item>
            );
          }
          return null;
        }}
      </Form.Item>
      <Divider />
      <Form.Item label={"Alignment"} name="align">
        <Radio.Group
          style={{ margin: "12px 0 0 0" }}
          options={[
            { label: <AlignLeftOutlined />, value: "left" },
            { label: <AlignCenterOutlined />, value: "center" },
            { label: <AlignRightOutlined />, value: "right" },
          ]}
          defaultValue="center"
          optionType="button"
        />
      </Form.Item>
      <Divider />

      <LinkInputFields />
      <Divider />
      <ContentID />
      <ContentPadding />
    </>
  );
};
